import {createContext, type ReactNode, useCallback, useContext, useMemo, useState} from 'react'

type CommentEditStates = Readonly<Record<string, boolean>>

type IssueCommentEditsContextType = {
  startCommentEdit: (id: string) => void
  cancelCommentEdit: (id: string) => void
  clearCommentEditStates: () => void
  isAnyCommentEditActive: boolean
}

const IssueCommentEditsContext = createContext<IssueCommentEditsContextType | null>(null)

type IssueCommentEditsContextProviderType = {
  children: ReactNode
}

const defaultEmptyState = {}
export function IssueCommentEditsContextProvider({children}: IssueCommentEditsContextProviderType) {
  const [commentEditStates, setCommentEditStates] = useState<CommentEditStates>(defaultEmptyState)

  const startCommentEdit = useCallback((id: string) => {
    setCommentEditStates(previousCommentEditStates => {
      if (previousCommentEditStates[id]) return previousCommentEditStates
      return {
        ...previousCommentEditStates,
        [id]: true,
      }
    })
  }, [])

  const cancelCommentEdit = useCallback((id: string) => {
    setCommentEditStates(previousCommentEditStates => {
      if (!previousCommentEditStates[id]) return previousCommentEditStates
      return {
        ...previousCommentEditStates,
        [id]: false,
      }
    })
  }, [])

  const isAnyCommentEditActive = useMemo(() => {
    return Object.values(commentEditStates).some(s => s)
  }, [commentEditStates])

  const clearCommentEditStates = useCallback(() => {
    setCommentEditStates(defaultEmptyState)
  }, [])

  const contextValue: IssueCommentEditsContextType = useMemo(() => {
    return {
      startCommentEdit,
      cancelCommentEdit,
      clearCommentEditStates,
      isAnyCommentEditActive,
    }
  }, [startCommentEdit, cancelCommentEdit, clearCommentEditStates, isAnyCommentEditActive])

  return <IssueCommentEditsContext.Provider value={contextValue}>{children}</IssueCommentEditsContext.Provider>
}

export const useIssueCommentEditsContext = () => {
  const context = useContext(IssueCommentEditsContext)
  if (!context) {
    throw new Error('useIssueCommentEditsContext must be used within a IssueCommentEditsContextProvider.')
  }

  return context
}

try{ IssueCommentEditsContext.displayName ||= 'IssueCommentEditsContext' } catch {}
try{ IssueCommentEditsContextProvider.displayName ||= 'IssueCommentEditsContextProvider' } catch {}